import {
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
  type Column,
  type ColumnDef,
  type ColumnFiltersState,
  type FilterFn,
  type OnChangeFn,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import {
  Fragment,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type UIEvent,
} from "react";

import { Button } from "./Button";
import { downloadCsv } from "./csv";
import { MultiSelect } from "./MultiSelect";
import { Text } from "./Typography";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[tabindex]",
].join(",");

/** Row-activation guard: clicks/keys whose target sits inside an interactive
 *  descendant (a cell button, link, or field) belong to that control, never
 *  to the row's onRowClick. */
function isInteractiveDescendantEvent(
  event: MouseEvent<HTMLTableRowElement> | KeyboardEvent<HTMLTableRowElement>,
): boolean {
  if (!(event.target instanceof Element)) {
    return false;
  }
  const interactiveTarget = event.target.closest(FOCUSABLE_SELECTOR);
  return (
    interactiveTarget !== null && interactiveTarget !== event.currentTarget
  );
}

interface PendingVirtualFocus {
  rowIndex: number;
  direction: "forward" | "backward";
}

/** UI-LAW §4b.3 opt-in row virtualization. Fixed row height (UI-LAW §3 = 40px) makes
 *  the window math exact, so no measuring virtualizer library is needed.
 *
 *  a11y: virtualized-out rows are absent from the DOM. Row count/position is
 *  exposed to assistive tech via aria-rowcount/aria-rowindex. Boundary focus
 *  sentinels scroll the next/previous virtual window into view so interactive
 *  cells stay reachable by keyboard Tab without introducing a managed grid. */
interface DataTableVirtualBaseOptions {
  /** True when virtualized rows can render focusable descendants (e.g. a
   *  cell button/link column). Implied automatically when `onRowClick` is
   *  set, because row-click rows are themselves focusable (tabIndex 0). */
  interactiveRows?: boolean;
  /** Extra rows rendered above/below the viewport. Default 8. */
  overscan?: number;
  /** Fixed row height in px. Default 40 (UI-LAW §3). */
  rowHeight?: number;
}

/** Fixed-height mode: the region is a maxHeight-bounded scroll viewport. */
export interface DataTableVirtualFixedHeightOptions extends DataTableVirtualBaseOptions {
  /** Height-bounded scroll viewport, in px. Also sizes the window. */
  maxHeight: number;
  fill?: false;
}

/** Fill mode (UI-LAW §4b.3 — chrome fixed, content scrolls): the region
 *  flexes into the remaining height of its flex-column parent (e.g.
 *  `Page variant="workspace"`). A ResizeObserver measures the flexed region
 *  and feeds the SAME window math that maxHeight drives in fixed mode. */
export interface DataTableVirtualFillOptions extends DataTableVirtualBaseOptions {
  fill: true;
  /** Viewport height used before the first measurement and when
   *  ResizeObserver is unavailable (e.g. happy-dom). Default 600. */
  maxHeight?: number;
}

export type DataTableVirtualOptions =
  | DataTableVirtualFixedHeightOptions
  | DataTableVirtualFillOptions;

/** Fill-mode viewport height before measurement / without ResizeObserver. */
const VIRTUAL_FILL_FALLBACK_HEIGHT = 600;

/** UI-LAW §4c List opt-in client-side CSV export of loaded rows. */
export interface DataTableCsvExportOptions {
  /** Download file name. Default "export.csv". */
  fileName?: string;
  /** Export button label. Default "Export CSV". */
  label?: string;
}

/**
 * Where a table's scrollport sat, in row-identity coordinates rather than
 * pixels. Lists in this app head-insert (keyset DESC), so a raw pixel offset
 * points at a different row after any refetch; the row id survives that.
 */
export interface DataTableViewAnchor {
  /** `getRowId` of the row at the top of the scrollport, when one existed. */
  anchorRowId: string | null;
  /** That row's index in the sorted/filtered row model when captured. */
  anchorIndex: number;
  /** Pixels the scrollport had passed the anchor row's top edge (>= 0). */
  anchorOffset: number;
}

/** `DataTableViewAnchor` plus the sort/filter state that produced it. */
export interface DataTableViewportState extends DataTableViewAnchor {
  columnFilters: ColumnFiltersState;
  sorting: SortingState;
}

/** Row height assumed by the virtual window when a caller omits one. */
export const DEFAULT_VIRTUAL_ROW_HEIGHT = 40;

/** Overscan rows rendered beyond the viewport when a caller omits one. */
export const DEFAULT_VIRTUAL_OVERSCAN = 8;

/** Payload of `onReachEnd`: the loaded row count the evaluation saw, in the
 *  sorted/filtered row model, so a caller can tell a fetch that grew the
 *  list from one that yielded nothing. */
export interface DataTableReachEndEvent {
  rowCount: number;
}

/**
 * Whether a virtual window ending at `scrollTop + viewportHeight` sits within
 * `thresholdRows` of the last loaded row. A pure row-count computation over
 * the window geometry DataTable already owns — never an IntersectionObserver
 * on a DOM sentinel: virtualized-out rows are absent from the DOM exactly
 * when they matter, and keyboard users reach the end through
 * `focusVirtualRow`, which writes the same scroll position the pointer does.
 */
export function isWithinReachEnd(geometry: {
  rowCount: number;
  rowHeight: number;
  scrollTop: number;
  thresholdRows: number;
  viewportHeight: number;
}): boolean {
  const lastVisibleRow = Math.ceil(
    (geometry.scrollTop + geometry.viewportHeight) / geometry.rowHeight,
  );
  return lastVisibleRow + geometry.thresholdRows >= geometry.rowCount;
}

/**
 * Anchor -> pixels, against the row model actually rendered. Falls back to the
 * stored index when the anchor row is gone (deleted, or filtered out by a
 * restored filter), and clamps into range.
 */
function resolveAnchorScrollTop(
  anchor: DataTableViewAnchor | undefined,
  rows: Array<{ id: string }>,
  rowHeight: number,
): number {
  if (anchor === undefined) {
    return 0;
  }
  const byId =
    anchor.anchorRowId === null
      ? -1
      : rows.findIndex((row) => row.id === anchor.anchorRowId);
  const maxIndex = Math.max(0, rows.length - 1);
  const index =
    byId >= 0 ? byId : Math.min(Math.max(0, anchor.anchorIndex), maxIndex);
  return Math.max(0, index * rowHeight + anchor.anchorOffset);
}

export interface DataTableProps<TData> extends Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> {
  ariaLabel?: string;
  /** Column ids constrained to the table's tokenized wide-cell bound. */
  boundedColumns?: string[];
  columns: Array<ColumnDef<TData>>;
  data: TData[];
  emptyMessage?: string;
  getRowId?: (originalRow: TData, index: number, parent?: Row<TData>) => string;
  isRowExpanded?: (row: Row<TData>) => boolean;
  onRowClick?: (row: Row<TData>) => void;
  /** Warm row-owned reads when pointer or keyboard focus signals intent. */
  onRowIntent?: (row: Row<TData>) => void;
  renderExpandedRow?: (row: Row<TData>) => ReactNode;

  // UI-LAW §4 DataTable column sorting over already-loaded rows.
  /** Enable click-header client-side sorting (uncontrolled). */
  enableSorting?: boolean;
  /** Initial sort for the uncontrolled case. */
  defaultSorting?: SortingState;
  /** Restored sort for the uncontrolled case. */
  initialSorting?: SortingState;
  /** Controlled sort state. Providing this enables sorting. */
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;

  // Faceted / multi-select filtering (client-side, over loaded rows): these
  // are the per-column facet selects UI-LAW §4b.4 names as the pattern being
  // replaced, rendered with the §4 MultiSelect primitive.
  /** Column ids to expose a faceted MultiSelect filter for. */
  filterableColumns?: string[];
  /** Whether the legacy inline facet toolbar is rendered. Default true. */
  showFilterToolbar?: boolean;
  /** Controlled column-filter state. Providing this enables filtering. */
  columnFilters?: ColumnFiltersState;
  /** Restored filters for the uncontrolled case. */
  initialColumnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;

  // UI-LAW §4b.3 row virtualization (client-side rendering optimization).
  virtual?: DataTableVirtualOptions;
  /**
   * Fired when the virtual window comes within `endThresholdRows` of the last
   * loaded row — from the scroll handler and from `focusVirtualRow` (keyboard
   * reach through the boundary sentinels). Never fires on a non-virtualized
   * table: it owns no scrollport. A scroll-position computation, never an
   * IntersectionObserver sentinel.
   */
  onReachEnd?: (event: DataTableReachEndEvent) => void;
  /** Rows before the end at which `onReachEnd` fires. Default: the virtual
   *  overscan, so the fetch starts as the last rendered band scrolls in.
   *  ASSUMED(no doc states a pre-fetch distance; the overscan is the one
   *  row-count the window already reasons in, so it is not a new magic
   *  number.) */
  endThresholdRows?: number;
  /**
   * Viewport anchor restored when this table mounts. Resolved against this
   * table's OWN sorted/filtered row model (see `resolveAnchorScrollTop`), so a
   * restored sort or filter lands on the anchor row rather than on whatever
   * row happens to occupy the stored index in the caller's raw item array.
   */
  initialAnchor?: DataTableViewAnchor;
  /**
   * Emitted on scroll and once after the row model settles following a sort or
   * filter change — never once per render. The payload is already in the
   * anchor coordinates `initialAnchor` reads back.
   */
  onViewportChange?: (viewport: DataTableViewportState) => void;

  // UI-LAW §4c List CSV export of loaded rows (client-side, no dep).
  csvExport?: boolean | DataTableCsvExportOptions;
}

/** Safe display/serialization string for an arbitrary cell/facet value. */
function toText(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value);
  }
  if (value !== null && typeof value === "object") {
    return JSON.stringify(value);
  }
  return "";
}

/** Multi-value faceted filter: keep a row when its cell value is one of the
 *  selected values; an empty selection is a no-op (auto-removed). */
const facetedArrayIncludesSome: FilterFn<unknown> = (
  row,
  columnId,
  filterValue: unknown,
) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) {
    return true;
  }
  return (filterValue as string[]).includes(toText(row.getValue(columnId)));
};
facetedArrayIncludesSome.autoRemove = (value: unknown) =>
  !Array.isArray(value) || value.length === 0;

function getColumnKey<TData>(column: ColumnDef<TData>): string | undefined {
  const candidate = column as { accessorKey?: unknown; id?: unknown };
  if (typeof candidate.id === "string") {
    return candidate.id;
  }
  if (typeof candidate.accessorKey === "string") {
    return candidate.accessorKey;
  }
  return undefined;
}

function resolveCsvOptions(
  csvExport: boolean | DataTableCsvExportOptions | undefined,
): Required<DataTableCsvExportOptions> | undefined {
  if (!csvExport) {
    return undefined;
  }
  const options = csvExport === true ? {} : csvExport;
  return {
    fileName: options.fileName ?? "export.csv",
    label: options.label ?? "Export CSV",
  };
}

function isFocusable(element: HTMLElement): boolean {
  return (
    element.tabIndex >= 0 &&
    !element.hasAttribute("disabled") &&
    !element.hasAttribute("hidden") &&
    element.getAttribute("aria-hidden") !== "true"
  );
}

function getVirtualRowIndex(rowElement: HTMLElement): number | undefined {
  const raw = rowElement.getAttribute("data-marker-virtual-row-index");
  if (raw === null) {
    return undefined;
  }
  const parsed = Number(raw);
  return Number.isInteger(parsed) ? parsed : undefined;
}

function findFocusableInVirtualWindow(
  container: HTMLElement,
  pending: PendingVirtualFocus,
): HTMLElement | undefined {
  const rows = Array.from(
    container.querySelectorAll<HTMLElement>("[data-marker-virtual-row-index]"),
  )
    .map((rowElement) => ({
      rowElement,
      rowIndex: getVirtualRowIndex(rowElement),
    }))
    .filter(
      (
        row,
      ): row is {
        rowElement: HTMLElement;
        rowIndex: number;
      } => row.rowIndex !== undefined,
    )
    .filter((row) =>
      pending.direction === "forward"
        ? row.rowIndex >= pending.rowIndex
        : row.rowIndex <= pending.rowIndex,
    )
    .sort((a, b) =>
      pending.direction === "forward"
        ? a.rowIndex - b.rowIndex
        : b.rowIndex - a.rowIndex,
    );

  for (const row of rows) {
    // A row-click row (tabIndex 0) is itself focusable and precedes its
    // descendants in tab order.
    const focusable = [
      row.rowElement,
      ...Array.from(
        row.rowElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ),
    ].filter(isFocusable);
    const target =
      pending.direction === "forward" ? focusable[0] : focusable.at(-1);
    if (target) {
      return target;
    }
  }
  return undefined;
}

/** Separator for the `filterableColumns` content key. Column ids are schema
 *  field names, so a control character can never collide with one. */
const FILTER_ID_SEPARATOR = "\u001f";

/** One faceted column filter.
 *
 *  It lives in its own component so the option set can be memoized PER COLUMN:
 *  table-core memoizes a column's faceted-value Map on that column's faceted
 *  row model, so the Map's identity is the exact "these facets changed"
 *  signal — and a per-column hook is the only way to key on it, since a
 *  dependency array cannot vary in length with the number of filterable
 *  columns. Derived in the toolbar's render body instead, the whole set was
 *  rebuilt (and `localeCompare`-sorted, O(u log u) over every distinct loaded
 *  value) on every render of the table, including every scroll frame. */
function DataTableFacetFilter<TData>({ column }: { column: Column<TData> }) {
  const facetedValues = column.getFacetedUniqueValues();
  const options = useMemo(
    () =>
      Array.from(facetedValues.entries())
        .filter(
          ([value]) => value !== null && value !== undefined && value !== "",
        )
        .map(([value, count]) => ({
          label: `${toText(value)} (${String(count)})`,
          value: toText(value),
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [facetedValues],
  );
  const headerDef = column.columnDef.header;
  const label = typeof headerDef === "string" ? headerDef : column.id;
  const selected = (column.getFilterValue() as string[] | undefined) ?? [];

  return (
    <MultiSelect
      label={label}
      onChange={(next) => {
        column.setFilterValue(next.length > 0 ? next : undefined);
      }}
      options={options}
      placeholder={`Filter ${label.toLowerCase()}…`}
      value={selected}
    />
  );
}

export function DataTable<TData>({
  ariaLabel,
  boundedColumns,
  className,
  columns,
  data,
  emptyMessage = "No rows",
  getRowId,
  isRowExpanded,
  onRowClick,
  onRowIntent,
  renderExpandedRow,
  enableSorting = false,
  defaultSorting,
  initialSorting,
  sorting,
  onSortingChange,
  filterableColumns,
  showFilterToolbar = true,
  columnFilters,
  initialColumnFilters,
  onColumnFiltersChange,
  virtual,
  initialAnchor,
  onViewportChange,
  onReachEnd,
  endThresholdRows,
  csvExport,
  onScroll,
  ...props
}: DataTableProps<TData>) {
  const isSortingControlled = sorting !== undefined;
  const sortingEnabled = enableSorting || isSortingControlled;
  const viewportEmitPendingRef = useRef(false);

  const [internalSorting, setInternalSorting] = useState<SortingState>(
    () => initialSorting ?? defaultSorting ?? [],
  );
  const effectiveSorting = isSortingControlled ? sorting : internalSorting;
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next =
      typeof updater === "function" ? updater(effectiveSorting) : updater;
    if (!isSortingControlled) {
      setInternalSorting(next);
    }
    onSortingChange?.(next);
    // The sorted row model has not been rebuilt yet; emitting here would
    // capture an anchor from the OLD ordering. Defer to the layout effect.
    viewportEmitPendingRef.current = true;
  };

  const isFiltersControlled = columnFilters !== undefined;
  const filteringEnabled =
    (filterableColumns?.length ?? 0) > 0 || isFiltersControlled;
  const [internalFilters, setInternalFilters] = useState<ColumnFiltersState>(
    () => initialColumnFilters ?? [],
  );
  const effectiveFilters = isFiltersControlled
    ? columnFilters
    : internalFilters;
  const handleFiltersChange: OnChangeFn<ColumnFiltersState> = (updater) => {
    const next =
      typeof updater === "function" ? updater(effectiveFilters) : updater;
    if (!isFiltersControlled) {
      setInternalFilters(next);
    }
    onColumnFiltersChange?.(next);
    viewportEmitPendingRef.current = true;
  };

  // Every call site passes `filterableColumns` as an inline literal, so its
  // array identity churns on every render. Key the derived ids on their
  // CONTENT instead: `resolvedColumns` then keeps its identity, and with it
  // every table-core Column object — which is what owns the memoized faceted
  // row model and faceted-value Map. Keyed on identity, those were rebuilt
  // (O(rows) per filterable column) on every render.
  const filterColumnKey = (filterableColumns ?? []).join(FILTER_ID_SEPARATOR);
  const filterColumnIds = useMemo(
    () =>
      filterColumnKey === "" ? [] : filterColumnKey.split(FILTER_ID_SEPARATOR),
    [filterColumnKey],
  );
  const filterKeys = useMemo(() => new Set(filterColumnIds), [filterColumnIds]);
  const boundedColumnIds = useMemo(
    () => new Set(boundedColumns ?? []),
    [boundedColumns],
  );
  const resolvedColumns = useMemo<Array<ColumnDef<TData>>>(() => {
    if (filterKeys.size === 0) {
      return columns;
    }
    return columns.map((column) => {
      const key = getColumnKey(column);
      if (key !== undefined && filterKeys.has(key)) {
        return {
          ...column,
          enableColumnFilter: true,
          filterFn: facetedArrayIncludesSome as FilterFn<TData>,
        };
      }
      return column;
    });
  }, [columns, filterKeys]);

  const table = useReactTable<TData>({
    columns: resolvedColumns,
    data,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    ...(sortingEnabled
      ? {
          onSortingChange: handleSortingChange,
          getSortedRowModel: getSortedRowModel(),
        }
      : {}),
    ...(filteringEnabled
      ? {
          onColumnFiltersChange: handleFiltersChange,
          getFilteredRowModel: getFilteredRowModel(),
          getFacetedRowModel: getFacetedRowModel(),
          getFacetedUniqueValues: getFacetedUniqueValues(),
        }
      : {}),
    ...(sortingEnabled || filteringEnabled
      ? {
          state: {
            ...(sortingEnabled ? { sorting: effectiveSorting } : {}),
            ...(filteringEnabled ? { columnFilters: effectiveFilters } : {}),
          },
        }
      : {}),
  });

  const virtualRowHeight = virtual?.rowHeight ?? DEFAULT_VIRTUAL_ROW_HEIGHT;
  // Resolved once, from the row model the FIRST render will draw (the restored
  // sort/filters already seeded the state above), so that render computes the
  // right topPad/bottomPad window instead of starting at the top and jumping.
  const [restoredScrollTop] = useState(() =>
    resolveAnchorScrollTop(
      initialAnchor,
      table.getRowModel().rows,
      virtualRowHeight,
    ),
  );
  const [scrollTop, setScrollTop] = useState(restoredScrollTop);

  // UI-LAW §4b.3 fill mode: measured flex-region height, or null before
  // the first ResizeObserver callback (and forever in environments without
  // ResizeObserver, where the maxHeight/600px fallback sizes the window).
  const isFillVirtual = virtual?.fill === true;
  const [measuredFillHeight, setMeasuredFillHeight] = useState<number | null>(
    null,
  );

  const csvOptions = resolveCsvOptions(csvExport);
  const columnCount = table.getAllColumns().length;
  const allRows = table.getRowModel().rows;
  const headerGroups = table.getHeaderGroups();

  // UI-LAW §4b.3 fixed-height windowing over the loaded rows.
  const isVirtual = virtual !== undefined && allRows.length > 0;
  const overscan = virtual?.overscan ?? DEFAULT_VIRTUAL_OVERSCAN;
  const viewportHeight =
    virtual === undefined
      ? 0
      : virtual.fill === true
        ? (measuredFillHeight ??
          virtual.maxHeight ??
          VIRTUAL_FILL_FALLBACK_HEIGHT)
        : virtual.maxHeight;
  let topPad = 0;
  let bottomPad = 0;
  let windowRows = allRows;
  let windowStart = 0;
  let windowEnd = allRows.length;
  if (isVirtual) {
    const visible = Math.max(1, Math.ceil(viewportHeight / virtualRowHeight));
    const maxStart = Math.max(0, allRows.length - visible);
    const rawStart = Math.floor(scrollTop / virtualRowHeight) - overscan;
    const start = Math.min(Math.max(0, rawStart), maxStart);
    const end = Math.min(allRows.length, start + visible + overscan * 2);
    topPad = start * virtualRowHeight;
    bottomPad = (allRows.length - end) * virtualRowHeight;
    windowRows = allRows.slice(start, end);
    windowStart = start;
    windowEnd = end;
  }

  // Virtualization a11y: only a window of rows lives in the DOM, so the browser
  // cannot derive the true totals. Expose them explicitly on the table role and
  // stamp each rendered row with its absolute position, so assistive tech
  // announces "row X of N" correctly. Per ARIA these are 1-based and count the
  // header row(s): header row is rowindex 1, the first data row is 2. Emitted
  // ONLY in virtualized mode — the non-virtualized render keeps every row in the
  // DOM (browser derives these natively) and must stay byte-identical.
  const ariaRowCount = isVirtual
    ? allRows.length + headerGroups.length
    : undefined;

  // Dynamic geometry (scroll bound + spacer heights) is genuinely data-driven,
  // so it is applied imperatively (the Progress/AudioPlayer precedent) rather
  // than through banned inline style attributes.
  const containerRef = useRef<HTMLDivElement>(null);
  const topSpacerRef = useRef<HTMLTableCellElement>(null);
  const bottomSpacerRef = useRef<HTMLTableCellElement>(null);
  const pendingVirtualFocusRef = useRef<PendingVirtualFocus | null>(null);
  const hasRestoredScrollRef = useRef(false);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    if (virtual !== undefined && virtual.fill !== true) {
      container.style.maxHeight = `${String(virtual.maxHeight)}px`;
    } else {
      // Fill mode: flex layout owns the region height, never max-height.
      container.style.removeProperty("max-height");
    }
  }, [virtual]);

  // UI-LAW §4b.3 fill mode observes the flexed region so viewport resizes and pane
  // changes re-drive the window math. ResizeObserver is absent in happy-dom;
  // the maxHeight/VIRTUAL_FILL_FALLBACK_HEIGHT fallback covers that case.
  useLayoutEffect(() => {
    if (!isFillVirtual) {
      setMeasuredFillHeight(null);
      return;
    }
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (!entry) {
        return;
      }
      const nextHeight = entry.contentRect.height;
      if (nextHeight > 0) {
        setMeasuredFillHeight(nextHeight);
      }
    });
    observer.observe(container);
    return () => {
      observer.disconnect();
    };
  }, [isFillVirtual]);

  useLayoutEffect(() => {
    if (topSpacerRef.current) {
      topSpacerRef.current.style.height = `${String(topPad)}px`;
    }
    if (bottomSpacerRef.current) {
      bottomSpacerRef.current.style.height = `${String(bottomPad)}px`;
    }
  }, [topPad, bottomPad]);

  // This must follow the spacer effect: virtual spacer heights create the
  // scroll range, and browsers otherwise clamp the restored position to zero.
  useLayoutEffect(() => {
    if (hasRestoredScrollRef.current || initialAnchor === undefined) {
      return;
    }
    const container = containerRef.current;
    if (container) {
      container.scrollTop = restoredScrollTop;
      hasRestoredScrollRef.current = true;
    }
  }, [initialAnchor, restoredScrollTop]);

  useLayoutEffect(() => {
    const pending = pendingVirtualFocusRef.current;
    if (!pending || !isVirtual) {
      return;
    }
    pendingVirtualFocusRef.current = null;

    const container = containerRef.current;
    const target = container
      ? findFocusableInVirtualWindow(container, pending)
      : undefined;
    target?.focus({ preventScroll: true });
  }, [isVirtual, windowStart, windowRows.length]);

  // Reach-end evaluation against the CURRENT window geometry. Called from the
  // scroll handler and from `focusVirtualRow`; never during render.
  const reachEndThreshold = endThresholdRows ?? overscan;
  const evaluateReachEnd = (nextScrollTop: number) => {
    if (
      onReachEnd === undefined ||
      !isVirtual ||
      !isWithinReachEnd({
        rowCount: allRows.length,
        rowHeight: virtualRowHeight,
        scrollTop: nextScrollTop,
        thresholdRows: reachEndThreshold,
        viewportHeight,
      })
    ) {
      return;
    }
    onReachEnd({ rowCount: allRows.length });
  };

  // Virtual focus reach is a sentinel-only table affordance, not a managed
  // ARIA grid model; docs/09 owns this frontend law. Writing `scrollTop`
  // dispatches a scroll event in a browser but not in every environment, so
  // the keyboard path evaluates reach-end itself rather than relying on the
  // scroll handler observing the write.
  const focusVirtualRow = (
    rowIndex: number,
    direction: PendingVirtualFocus["direction"],
  ) => {
    if (!isVirtual) {
      return;
    }
    const targetIndex = Math.min(Math.max(0, rowIndex), allRows.length - 1);
    pendingVirtualFocusRef.current = {
      rowIndex: targetIndex,
      direction,
    };

    const container = containerRef.current;
    if (container) {
      const nextScrollTop = targetIndex * virtualRowHeight;
      container.scrollTop = nextScrollTop;
      setScrollTop(nextScrollTop);
      evaluateReachEnd(nextScrollTop);
    }
  };

  const handleExport = () => {
    const exportRows = table.getRowModel().rows;
    const leafColumns = table
      .getAllLeafColumns()
      .filter((column) => column.getIsVisible() && column.accessorFn != null);
    const header = leafColumns.map((column) => {
      const headerDef = column.columnDef.header;
      return typeof headerDef === "string" ? headerDef : column.id;
    });
    downloadCsv(csvOptions?.fileName ?? "export.csv", [
      header,
      ...exportRows.map((row) =>
        leafColumns.map((column) => toText(row.getValue(column.id))),
      ),
    ]);
  };

  const classes = ["marker-data-table"];
  if (isVirtual) {
    classes.push("marker-data-table--virtualized");
  }
  if (isFillVirtual) {
    // Applied whenever fill is requested (even with zero rows) so the region
    // holds its flexed height instead of collapsing around the empty state.
    classes.push("marker-data-table--fill");
  }
  if (className) {
    classes.push(className);
  }

  // Anchors against `allRows` — the SORTED, FILTERED row model — which is the
  // same model `resolveAnchorScrollTop` reads back on restore. Called from the
  // scroll handler and from the deferred sort/filter effect below; never
  // during render.
  const emitViewportChange = (nextScrollTop: number) => {
    if (!onViewportChange) {
      return;
    }
    const anchorIndex = isVirtual
      ? Math.min(
          Math.max(0, Math.floor(nextScrollTop / virtualRowHeight)),
          Math.max(0, allRows.length - 1),
        )
      : 0;
    onViewportChange({
      anchorIndex,
      anchorOffset: nextScrollTop - anchorIndex * virtualRowHeight,
      anchorRowId: allRows[anchorIndex]?.id ?? null,
      columnFilters: effectiveFilters,
      sorting: effectiveSorting,
    });
  };

  // Syncs the caller's view-state store (an external system) with the row
  // model a sort or filter change just produced. Flag-guarded, so it is a
  // once-per-interaction emit, not a mirror of every render.
  useLayoutEffect(() => {
    if (!viewportEmitPendingRef.current) {
      return;
    }
    viewportEmitPendingRef.current = false;
    emitViewportChange(scrollTop);
  });

  const handleScroll = isVirtual
    ? (event: UIEvent<HTMLDivElement>) => {
        const nextScrollTop = event.currentTarget.scrollTop;
        setScrollTop(nextScrollTop);
        emitViewportChange(nextScrollTop);
        evaluateReachEnd(nextScrollTop);
        onScroll?.(event);
      }
    : onScroll;

  // Deliberately not memoized: `table.getColumn` must hand back the CURRENT
  // Column objects, whose memo caches are what `DataTableFacetFilter` keys on.
  const filterColumns = filterColumnIds
    .map((id) => table.getColumn(id))
    .filter((column) => column !== undefined);

  const showToolbar =
    csvOptions !== undefined || (showFilterToolbar && filteringEnabled);
  // Row-click rows are focusable descendants of the virtual window, so
  // onRowClick implies interactiveRows (sentinels must keep them reachable).
  const hasVirtualInteractiveRows =
    isVirtual && (virtual.interactiveRows === true || onRowClick !== undefined);
  const hasVirtualRowsBefore = hasVirtualInteractiveRows && windowStart > 0;
  const hasVirtualRowsAfter =
    hasVirtualInteractiveRows && windowEnd < allRows.length;

  return (
    <div
      aria-label={ariaLabel}
      className={classes.join(" ")}
      onScroll={handleScroll}
      ref={containerRef}
      role="region"
      {...props}
    >
      {showToolbar ? (
        <div className="marker-data-table__toolbar">
          {showFilterToolbar
            ? filterColumns.map((column) => (
                <DataTableFacetFilter column={column} key={column.id} />
              ))
            : null}
          {csvOptions ? (
            <div className="marker-data-table__export">
              <Button
                onClick={handleExport}
                size="sm"
                type="button"
                variant="secondary"
              >
                {csvOptions.label}
              </Button>
              <Text as="span" variant="muted">
                Loaded rows only
              </Text>
            </div>
          ) : null}
        </div>
      ) : null}
      {hasVirtualRowsBefore ? (
        <button
          aria-label="Load previous virtualized rows"
          className="marker-data-table__virtual-focus-sentinel"
          onFocus={() => {
            focusVirtualRow(windowStart - 1, "backward");
          }}
          type="button"
        />
      ) : null}
      <table aria-rowcount={ariaRowCount}>
        <thead>
          {headerGroups.map((headerGroup, groupIndex) => (
            <tr
              aria-rowindex={isVirtual ? groupIndex + 1 : undefined}
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header) => {
                const columnClassName = boundedColumnIds.has(header.column.id)
                  ? "marker-data-table__column--bounded"
                  : undefined;
                if (header.isPlaceholder) {
                  return <th className={columnClassName} key={header.id} />;
                }
                const content = flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                );
                if (!sortingEnabled || !header.column.getCanSort()) {
                  return (
                    <th className={columnClassName} key={header.id}>
                      {content}
                    </th>
                  );
                }
                const sorted = header.column.getIsSorted();
                const ariaSort: "ascending" | "descending" | "none" =
                  sorted === "asc"
                    ? "ascending"
                    : sorted === "desc"
                      ? "descending"
                      : "none";
                return (
                  <th
                    aria-sort={ariaSort}
                    className={columnClassName}
                    key={header.id}
                  >
                    <button
                      className="marker-data-table__sort"
                      onClick={header.column.getToggleSortingHandler()}
                      type="button"
                    >
                      {content}
                      <span
                        aria-hidden="true"
                        className="marker-data-table__sort-indicator"
                      >
                        {sorted === "asc" ? "↑" : sorted === "desc" ? "↓" : "↕"}
                      </span>
                    </button>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {allRows.length > 0 ? (
            <>
              {topPad > 0 ? (
                <tr aria-hidden="true" className="marker-data-table__spacer">
                  <td colSpan={columnCount} ref={topSpacerRef} />
                </tr>
              ) : null}
              {windowRows.map((row, offset) => (
                <Fragment key={row.id}>
                  {/* Row-click rows are keyboard-operable (docs/09 §6):
                      semantic table + simple focusable rows with Enter/Space
                      activation — never a managed ARIA grid. */}
                  <tr
                    aria-rowindex={
                      isVirtual
                        ? headerGroups.length + windowStart + offset + 1
                        : undefined
                    }
                    className={
                      onRowClick
                        ? "marker-data-table__row--interactive"
                        : undefined
                    }
                    data-marker-virtual-row-index={
                      isVirtual ? windowStart + offset : undefined
                    }
                    onClick={
                      onRowClick
                        ? (event) => {
                            if (isInteractiveDescendantEvent(event)) {
                              return;
                            }
                            onRowClick(row);
                          }
                        : undefined
                    }
                    onKeyDown={
                      onRowClick
                        ? (event) => {
                            if (event.key !== "Enter" && event.key !== " ") {
                              return;
                            }
                            if (isInteractiveDescendantEvent(event)) {
                              return;
                            }
                            event.preventDefault();
                            onRowClick(row);
                          }
                        : undefined
                    }
                    onFocusCapture={
                      onRowIntent
                        ? () => {
                            onRowIntent(row);
                          }
                        : undefined
                    }
                    onMouseEnter={
                      onRowIntent
                        ? () => {
                            onRowIntent(row);
                          }
                        : undefined
                    }
                    tabIndex={onRowClick ? 0 : isVirtual ? -1 : undefined}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        className={
                          boundedColumnIds.has(cell.column.id)
                            ? "marker-data-table__column--bounded"
                            : undefined
                        }
                        key={cell.id}
                      >
                        <div className="marker-data-table__cell-content">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                  {renderExpandedRow && isRowExpanded?.(row) === true ? (
                    <tr className="marker-data-table__expanded-row">
                      <td colSpan={columnCount}>
                        <div className="marker-data-table__expanded-content">
                          {renderExpandedRow(row)}
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              ))}
              {bottomPad > 0 ? (
                <tr aria-hidden="true" className="marker-data-table__spacer">
                  <td colSpan={columnCount} ref={bottomSpacerRef} />
                </tr>
              ) : null}
            </>
          ) : (
            <tr>
              <td className="marker-data-table__empty" colSpan={columnCount}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {hasVirtualRowsAfter ? (
        <button
          aria-label="Load next virtualized rows"
          className="marker-data-table__virtual-focus-sentinel"
          onFocus={() => {
            focusVirtualRow(windowEnd, "forward");
          }}
          type="button"
        />
      ) : null}
    </div>
  );
}
